"use client";

import { useState, useTransition } from "react";
import { followTheCall, unfollowTheCall } from "@/app/actions/the-call";
import { useSpotUser } from "@/app/_components/conditions/SpotUserProvider";
import { useShallowSearchParam } from "@/lib/hooks/use-shallow-search-param";
import { CALL_FOLLOW_BUTTON_TOUCH_TARGET_CLASS } from "./call-follow-button-state";
import {
  callSportFromUrlKey,
  CALL_SPORT_LABELS,
  type CallSport,
} from "@/lib/the-call/types";
import {
  normaliseSupProfileSurfaceKey,
  type SupProfileKey,
} from "@/lib/conditions/sport-profiles";

type Props = {
  spotId: string;
  spotName: string;
  category: "ski" | "summer" | "sailing";
  sportOverride?: CallSport;
  buttonLabel?: string;
  allowedSupProfiles?: readonly SupProfileKey[];
};

// "Get The Call": opt in to rare-conditions alerts for this spot and the
// sport currently shown on the page. Strictly opt-in, one tap to leave.
export function CallFollowButton({
  spotId,
  spotName,
  category,
  sportOverride,
  buttonLabel,
  allowedSupProfiles = [],
}: Props) {
  const { isLoggedIn, callSports } = useSpotUser();
  const urlSport = useShallowSearchParam("sport");
  const authorisedUrlSport = urlSport === null
    ? null
    : normaliseSupProfileSurfaceKey(urlSport, allowedSupProfiles);
  const sport = sportOverride ?? callSportFromUrlKey(authorisedUrlSport, category);

  // null until toggled; the fetched state is the default
  const [override, setOverride] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  const following = override[sport] ?? callSports.includes(sport);
  const spotType =
    category === "ski" ? "ski" : category === "sailing" ? "sailing" : "surf";

  function handleClick() {
    setError(null);

    if (!isLoggedIn) {
      setError("Sign up free to get The Call");
      return;
    }

    const next = !following;
    setOverride((prev) => ({ ...prev, [sport]: next }));

    startTransition(async () => {
      const result = next
        ? await followTheCall(spotId, spotType, sport)
        : await unfollowTheCall(spotId, sport);

      if (!result.success) {
        setOverride((prev) => ({ ...prev, [sport]: !next }));
        setError(result.error ?? "Something went wrong. Please try again.");
      }
    });
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleClick}
        disabled={isPending}
        title={
          following
            ? `You get The Call for ${CALL_SPORT_LABELS[sport].toLowerCase()} at ${spotName}`
            : `Get The Call when ${spotName} is about to be special`
        }
        className={[
          `inline-flex ${CALL_FOLLOW_BUTTON_TOUCH_TARGET_CLASS} items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors disabled:opacity-60`,
          following
            ? "border-gold/60 bg-gold/20 text-gold-light hover:bg-gold/30"
            : "border-white/20 bg-white/10 text-white/90 hover:bg-white/20",
        ].join(" ")}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill={following ? "currentColor" : "none"}
          aria-hidden="true"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" fill="none" />
        </svg>
        {following ? "You'll get The Call" : buttonLabel ?? "Get The Call"}
      </button>
      {error && (
        <p className="absolute left-0 top-full mt-1 whitespace-nowrap text-[11px] text-amber-300">
          {error}
        </p>
      )}
    </div>
  );
}
